import { SaveViewModal } from "@/pages/employees/index/SaveViewModal.tsx";
import { ApiEmployeeListView } from "@/types/employees/employee-view.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Box, FontAwesomeIcon, Modal, ModalProps, Stack, useModals } from "@skailer-com/uikit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ManageViewsModalProps extends ModalProps {
  views: ApiEmployeeListView[];
  onRename: (viewId: number, title: string) => Promise<void>;
  onDelete: (viewId: number, options?: { onSuccess?: () => void }) => void;
  onReorder: (
    viewIds: number[],
    options?: { onError?: () => void; onSettled?: () => void }
  ) => void;
}

export function ManageViewsModal({
  views,
  onRename,
  onDelete,
  onReorder,
  ...props
}: ManageViewsModalProps) {
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const [items, setItems] = useState(views);
  const [isReordering, setIsReordering] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const previous = items;
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered);
    setIsReordering(true);
    onReorder(
      reordered.map((view) => view.id),
      { onError: () => setItems(previous), onSettled: () => setIsReordering(false) }
    );
  };

  const startRename = (view: ApiEmployeeListView) => {
    openModal(SaveViewModal, {
      initialTitle: view.title,
      heading: t("core:rename_view"),
      submitLabel: t("common:save"),
      onSubmitView: async (title) => {
        await onRename(view.id, title);
        setItems((prev) => prev.map((item) => (item.id === view.id ? { ...item, title } : item)));
      },
    });
  };

  const handleDelete = (viewId: number) => {
    onDelete(viewId, {
      onSuccess: () => setItems((prev) => prev.filter((view) => view.id !== viewId)),
    });
  };

  useEffect(() => {
    if (items.length === 0) {
      close(props.id);
    }
  }, [items.length]);

  return (
    <Modal {...props} variant="middle" title={t("core:manage_views")} withCloser closeByEscEnabled>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="employee-views">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <Stack gap="sm">
                {items.map((view, index) => (
                  <Draggable
                    key={view.id}
                    draggableId={String(view.id)}
                    index={index}
                    isDragDisabled={isReordering}
                  >
                    {(dragProvided) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className="flex items-center justify-between gap-md px-4 py-2 border border-dark/5 rounded-xs bg-dark/5"
                      >
                        <span className="text-base font-semibold text-default">{view.title}</span>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() => handleDelete(view.id)}
                          >
                            <FontAwesomeIcon icon="fa-light fa-trash" className="text-icon-16" />
                          </button>
                          <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() => startRename(view)}
                          >
                            <FontAwesomeIcon icon="fa-light fa-pen" className="text-icon-16" />
                          </button>
                          <span
                            {...dragProvided.dragHandleProps}
                            className="flex items-center cursor-grab"
                          >
                            <FontAwesomeIcon
                              icon="fa-light fa-grip-dots-vertical"
                              className="text-icon-16"
                            />
                          </span>
                        </div>
                      </Box>
                    )}
                  </Draggable>
                ))}
              </Stack>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Modal>
  );
}
